/**
 * StreamInlinePlayer — renders the right element for on-page playback.
 *
 * Routes to one of three players based on what's available on the source:
 *   · <iframe>          — Twitch (live + VOD), YouTube (live + VOD), Kick (live)
 *   · <video> + hls.js  — Kick VODs (Kick has no public VOD iframe)
 *   · empty placeholder — no embeddable source
 *
 * No modal. Mounts in-panel and starts playing immediately.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import {
    loadTwitchPlayerSdk,
    loadYouTubeIframeApi,
    type TwitchPlayerInstance,
    type YouTubePlayerInstance,
} from "@/utils/streamPlayerSdk";
import { useTrackWatchSession, type WatchSessionContext } from "@/hooks/useTrackWatchSession";
import { useElectronAudioLift } from "@/utils/electronAudio";
import { getStreamEmbedUrl, getStreamExternalUrl, sanitize } from "./shared";

export type InlinePlatform = "TWITCH" | "YOUTUBE" | "KICK";

export interface InlinePlayerSource {
    platform: InlinePlatform;
    /* Live-channel identifiers */
    twitchChannelId?: string;
    youtubeChannelId?: string;
    youtubeLiveId?: string;
    kickChannelSlug?: string;
    isLive: boolean;
    /* VOD identifiers (when set, take precedence over the live-channel embed) */
    twitchVideoId?: string;
    /*
     * Twitch clip slug (NOT a VOD id). Different embed URL than VODs —
     * https://clips.twitch.tv/embed?clip=<slug>. Without this branch a clip
     * selection silently falls through to the channel iframe and plays the
     * live stream (or offline screen) instead of the clip.
     */
    twitchClipSlug?: string;
    youtubeVideoId?: string;
    kickHlsUrl?: string;
    title?: string;
    muted?: boolean;
    /* Watch-session context — carried onto the recorded StreamView for analytics + objective scoping. */
    surfaceId?: string;
    competitionId?: string;
    /*
     * Opt-in autoplay. Default false — see the autoplay=false rationale in the
     * embed builder (Twitch refuses + logs a noisy warning when the iframe
     * isn't painted at check time). Surfaces that mount the player already
     * visible (e.g. the Drop-In Featured hero, which lazy-mounts on
     * intersection) can set this true WITH muted to start playback immediately
     * and collapse the platform's paused-state pre-roll chrome (play button,
     * Follow/Sub overlay, title bar). Muted is required for browser autoplay.
     */
    autoplay?: boolean;
    /*
     * True when the player is mounted as a result of a deliberate user action
     * (clicking a carousel tile, hitting play) rather than an auto-mount on page
     * load. Watch-time tracking only starts on real intent, so a muted autoplay
     * that nobody chose to watch never accrues. Any in-player interaction also
     * satisfies intent, so this is a hint, not the only path.
     */
    viewerInitiated?: boolean;
}

/*
 * kickHlsUrl is sourced from a Kick webhook response that could in theory
 * carry an unexpected URL. Reject the obvious attack shapes before handing
 * it to hls.js or the native <video> element — both happily load arbitrary
 * cross-origin manifests.
 *
 * Hostname is NOT allowlisted: Kick's HLS manifests are served from AWS IVS
 * and CloudFront edge domains (e.g. *.live-video.net, *.cloudfront.net),
 * not from kick.com itself. A strict kick.com allowlist would false-reject
 * every legitimate Kick VOD. The meaningful frontend defense is rejecting
 * non-https schemes, unparsable URLs, raw-IP hosts, and localhost — all of
 * which would never appear in real Kick payloads but ARE the shapes a
 * compromised webhook would use to redirect playback.
 */
function isSafeKickHlsUrl(url: string): boolean {
    try {
        const u = new URL(url);
        if (u.protocol !== "https:") return false;
        if (!u.hostname) return false;
        if (u.hostname === "localhost") return false;
        /* Reject IPv4 literals (1.2.3.4) and IPv6 literals ([::1]) */
        if ((/^\d+\.\d+\.\d+\.\d+$/).test(u.hostname)) return false;
        if (u.hostname.startsWith("[")) return false;
        /*
         * Reject obvious bidi/lookalike confusion in the hostname
         * (consecutive dots are not valid hostnames; some spoofing tools
         * produce them, and rejecting them costs us nothing).
         */
        if (u.hostname.includes("..")) return false;
        return true;
    } catch {
        return false;
    }
}

interface Props {
    source: InlinePlayerSource;
}

function platformLabel(p: InlinePlatform): string {
    switch (p) {
        case "TWITCH": return "Twitch";
        case "YOUTUBE": return "YouTube";
        case "KICK": return "Kick";
    }
}

export const StreamInlinePlayer: React.FC<Props> = ({ source }) => {
    /*
     * Decide which ELEMENT to mount up front, so React only mounts one element
     * type per (source) change — avoids flicker when the parent re-renders due
     * to upstream state changes.
     *
     * The URLs themselves come from `getStreamEmbedUrl`, the one shared
     * builder. This memo owns only the routing decision, which is genuinely
     * this player's: a live Twitch channel gets the SDK-managed player (real
     * PLAY/PAUSE/ENDED + getMuted for the accessible watch-time model), a Kick
     * VOD gets hls.js because Kick has no public VOD iframe, and everything
     * else is a plain iframe.
     */
    const embed = useMemo<
        | { kind: "twitch-sdk"; channel: string; fallbackSrc: string }
        | { kind: "iframe"; src: string; youtubeApi?: boolean }
        | { kind: "hls"; src: string }
        | { kind: "none" }
    >(() => {
        /*
         * autoplay defaults to false here, unlike the builder's own default.
         * Twitch's player runs an internal visibility check before attempting
         * autoplay and refuses (even muted) when the iframe hasn't been fully
         * painted at check time, logging a noisy "Autoplay disabled. style
         * visibility" warning while still NOT autoplaying. So consumers that
         * mount the player hidden get autoplay=false (clean console, user
         * clicks play). Surfaces that mount it already-visible (the Featured
         * hero, lazy-mounted on intersection) opt into autoplay=true + muted.
         */
        const opts = { muted: source.muted, autoplay: source.autoplay ?? false };

        if (source.platform === "TWITCH") {
            if (source.twitchClipSlug || source.twitchVideoId) {
                return { kind: "iframe", src: getStreamEmbedUrl(source, opts) };
            }
            if (source.twitchChannelId) {
                /*
                 * Live channel → SDK-managed player (Twitch.Player). fallbackSrc
                 * is the equivalent bare iframe, used if the SDK fails to load.
                 */
                return {
                    kind: "twitch-sdk",
                    channel: sanitize(source.twitchChannelId),
                    fallbackSrc: getStreamEmbedUrl(source, opts),
                };
            }
        }
        if (source.platform === "YOUTUBE") {
            /*
             * youtubeApi appends enablejsapi=1 + origin so the YT IFrame Player
             * API can attach (onStateChange + isMuted) for the accessible
             * watch-time model — no render change, unlike Twitch.
             */
            const src = getStreamEmbedUrl(source, { ...opts, youtubeApi: true });
            if (src) return { kind: "iframe", src, youtubeApi: true };
        }
        if (source.platform === "KICK") {
            if (source.kickHlsUrl && isSafeKickHlsUrl(source.kickHlsUrl)) {
                return { kind: "hls", src: source.kickHlsUrl };
            }
            const src = getStreamEmbedUrl(source, opts);
            if (src) return { kind: "iframe", src };
        }
        return { kind: "none" };
    }, [source]);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    /*
     * Defer iframe mount until the wrapper is on-screen. IntersectionObserver
     * does NOT fire for `display:none` elements, so this guarantees we don't
     * mount into a hidden tab / collapsed panel and burn a Kasada anti-bot
     * challenge slot for no UX gain. Latches once visible — scroll-out
     * doesn't unmount.
     */
    /* viewerInitiated = a click put this on stage; mount now, don't lazy-gate. */
    const [hasBeenVisible, setHasBeenVisible] = useState(!!source.viewerInitiated);

    useEffect(() => {
        if (hasBeenVisible) return;
        const el = wrapperRef.current;
        if (!el) return;
        const observer = new IntersectionObserver(entries => {
            if (entries.some(e => e.isIntersecting)) {
                setHasBeenVisible(true);
                observer.disconnect();
            }
        }, {threshold: 0.01});
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasBeenVisible, embed]);

    /*
     * Wire hls.js → <video> only when we're in HLS mode. Safari natively
     * supports HLS via `<video src>`, so we short-circuit there.
     */
    /*
     * Key this effect on the HLS URL STRING, never the `embed` object. `embed` is
     * a useMemo over the `source` prop, which upstream is itself a useMemo with
     * many deps — so any unstable dep gives `embed` a fresh identity on every
     * parent re-render. Depending on `[embed]` therefore tore down and re-created
     * the Hls instance (and its enableWorker:true blob-URL worker) on EVERY render,
     * leaking an unbounded pile of blob-script workers + segment buffers (the
     * runaway "blob:… " requests + climbing memory). A primitive src recreates the
     * worker only when the actual stream changes.
     */
    const hlsSrc = embed.kind === "hls" ? embed.src : null;
    useEffect(() => {
        if (!hlsSrc) return;
        const video = videoRef.current;
        if (!video) return;

        const cleanupNative = () => {
            video.removeAttribute("src");
            video.load();
        };

        const canPlayNative = video.canPlayType("application/vnd.apple.mpegurl") !== "";
        if (canPlayNative) {
            video.src = hlsSrc;
            return cleanupNative;
        }
        if (!Hls.isSupported()) {
            video.src = hlsSrc;
            return cleanupNative;
        }
        const hls = new Hls({ enableWorker: true });
        hls.loadSource(hlsSrc);
        hls.attachMedia(video);
        return () => {
            hls.destroy(); cleanupNative();
        };
    }, [hlsSrc]);

    /*
     * ── Accessible watch-time engagement ──────────────────────────────────────
     * "Watching" = deliberate intent + video PLAYING + stream live, treated as
     * idle ONLY when the user is engaging via NEITHER sight nor sound (muted AND
     * tab-hidden). A deaf viewer (muted + visible) and a blind viewer (audible +
     * hidden) both keep counting. Kick exposes no player state, so it's coarse:
     * assume playing while mounted, no mute signal, idle on tab-hidden alone.
     */
    const trackContext = useMemo<WatchSessionContext | undefined>(() => {
        if (!source.isLive) return undefined;
        // Clips / VODs are not watch-time — only live channels.
        if (source.twitchClipSlug || source.twitchVideoId || source.youtubeVideoId) return undefined;
        const ctx = { surfaceId: source.surfaceId, competitionId: source.competitionId };
        if (source.platform === "TWITCH" && source.twitchChannelId) {
            return { platform: "TWITCH", channelId: source.twitchChannelId, ...ctx };
        }
        if (source.platform === "YOUTUBE" && (source.youtubeChannelId || source.youtubeLiveId)) {
            return { platform: "YOUTUBE", channelId: (source.youtubeChannelId || source.youtubeLiveId)!, ...ctx };
        }
        if (source.platform === "KICK" && source.kickChannelSlug) {
            return { platform: "KICK", channelId: source.kickChannelSlug, ...ctx };
        }
        return undefined;
    }, [source]);

    const canDetectMute = source.platform !== "KICK";
    const uid = useId().replace(/:/g, "");
    const twitchDomId = `twitch-player-${uid}`;
    const ytIframeId = `yt-player-${uid}`;

    // Kick has no play-state signal — treat as playing while mounted (coarse).
    const [playing, setPlaying] = useState<boolean>(source.platform === "KICK");
    const [muted, setMuted] = useState<boolean>(source.muted ?? true);
    const [interacted, setInteracted] = useState(false);
    const [visible, setVisible] = useState<boolean>(typeof document === "undefined" ? true : document.visibilityState === "visible");
    const [twitchSdkFailed, setTwitchSdkFailed] = useState(false);
    const markIntent = useCallback(() => setInteracted(true), []);
    const hasIntent = interacted || !!source.viewerInitiated;

    useEffect(() => {
        const onVis = () => setVisible(document.visibilityState === "visible");
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, []);

    /*
     * Detect the user engaging the EMBEDDED player. The players are iframes
     * (Twitch SDK / YouTube / Kick), and pointer events inside an iframe don't
     * bubble to us — so onPointerDown on the wrapper never fires when the user
     * taps the actual player (e.g. unmutes it). When they click/tap into the
     * iframe it becomes document.activeElement and the parent window blurs; that
     * focus shift is our accessibility-safe "deliberate intent" signal (any
     * interaction, any modality — not tied to sight or sound).
     */
    useEffect(() => {
        const onBlur = () => {
            window.setTimeout(() => {
                const active = document.activeElement;
                if (active?.tagName === "IFRAME" && wrapperRef.current?.contains(active)) {
                    setInteracted(true);
                }
            }, 0);
        };
        window.addEventListener("blur", onBlur);
        return () => window.removeEventListener("blur", onBlur);
    }, []);

    /*
     * Hold the SDK player instances so we can tear them down on unmount / channel
     * switch. Without this, LiveInYourRoster (which mounts a fresh player per
     * clicked tile) would leak a Twitch.Player / YT.Player — each keeping its own
     * injected iframe, postMessage listeners, and timers — every time the active
     * tile changes.
     */
    const twitchPlayerRef = useRef<TwitchPlayerInstance | null>(null);
    const ytPlayerRef = useRef<YouTubePlayerInstance | null>(null);

    /*
     * Stable primitive identities for the SDK effects. `embed` is a fresh object
     * on every render, so depending on it directly would re-run these effects
     * (tearing down + pausing the live player) on ANY unrelated re-render — e.g.
     * a periodic live-roster refetch in the parent. Keying on the channel/video
     * string instead means the player is (re)created only when it actually
     * changes, and normal re-renders leave a playing stream untouched.
     */
    const twitchChannel = embed.kind === "twitch-sdk" ? embed.channel : null;
    const youtubeApiSrc = embed.kind === "iframe" && embed.youtubeApi ? embed.src : null;

    // Twitch live → SDK player for real PLAY/PAUSE/ENDED + mute state.
    useEffect(() => {
        if (!twitchChannel || !hasBeenVisible || twitchSdkFailed) return;
        let disposed = false;
        let mutePoll: ReturnType<typeof setInterval> | null = null;
        loadTwitchPlayerSdk().then(Twitch => {
            if (disposed) return;
            const hostname = window.location.hostname;
            const player: TwitchPlayerInstance = new Twitch.Player(twitchDomId, {
                channel: twitchChannel,
                parent: [hostname],
                muted: source.muted ?? true,
                autoplay: source.autoplay ?? true,
                width: "100%",
                height: "100%",
            });
            twitchPlayerRef.current = player;
            player.addEventListener(Twitch.Player.PLAY, () => setPlaying(true));
            player.addEventListener(Twitch.Player.PAUSE, () => setPlaying(false));
            player.addEventListener(Twitch.Player.ENDED, () => setPlaying(false));
            player.addEventListener(Twitch.Player.OFFLINE, () => setPlaying(false));
            mutePoll = setInterval(() => {
                try {
                    setMuted(player.getMuted());
                } catch { /* player torn down */ }
            }, 2000);
        })
            .catch(() => {
                if (!disposed) setTwitchSdkFailed(true);
            });
        return () => {
            disposed = true;
            if (mutePoll) clearInterval(mutePoll);
            /*
             * Twitch.Player has no documented destroy(); pause it and clear the
             * injected iframe out of the (React-owned) mount div so a re-mount
             * can't stack a second player. Clearing children React never created
             * is safe — React only tracks the empty div itself.
             */
            try {
                twitchPlayerRef.current?.pause?.();
            } catch { /* already gone */ }
            twitchPlayerRef.current = null;
            const mount = document.getElementById(twitchDomId);
            if (mount) mount.innerHTML = "";
        };
    }, [twitchChannel, hasBeenVisible, twitchSdkFailed, twitchDomId, source.muted, source.autoplay]);

    // YouTube live → attach the IFrame API to the existing iframe (no render change).
    useEffect(() => {
        if (!youtubeApiSrc || !hasBeenVisible) return;
        let disposed = false;
        let mutePoll: ReturnType<typeof setInterval> | null = null;
        loadYouTubeIframeApi().then(YT => {
            if (disposed) return;
            const player: YouTubePlayerInstance = new YT.Player(ytIframeId, {
                events: {
                    onStateChange: (e: { data: number }) => setPlaying(e.data === YT.PlayerState.PLAYING),
                },
            });
            ytPlayerRef.current = player;
            mutePoll = setInterval(() => {
                try {
                    if (player.isMuted) setMuted(player.isMuted());
                } catch { /* player torn down */ }
            }, 2000);
        })
            .catch(() => { /* no API → falls back to coarse (no play-state) */ });
        return () => {
            disposed = true;
            if (mutePoll) clearInterval(mutePoll);
            /*
             * destroy() releases the YT player + its postMessage listener; runs
             * before React unmounts the iframe, wrapped in case it already went.
             */
            try {
                ytPlayerRef.current?.destroy?.();
            } catch { /* already gone */ }
            ytPlayerRef.current = null;
        };
    }, [youtubeApiSrc, hasBeenVisible, ytIframeId]);

    const idle = !visible && (muted || !canDetectMute);
    const isWatching = !!trackContext && hasIntent && playing && source.isLive && !idle;
    useTrackWatchSession(isWatching, trackContext);

    /*
     * Electron: lift the shell's blanket renderer audio mute while this player
     * is genuinely audible, so an unmuted stream produces sound in the app
     * (see @/utils/electronAudio for why the renderer starts muted). Gate on
     * real intent: only once the user has engaged the player, and — where we
     * have a true mute signal (Twitch SDK / YouTube API) — only while unmuted.
     * Sources with no mute signal (Twitch clip/VOD iframes, Kick, HLS) fall
     * back to intent alone. No-ops on plain web.
     */
    const hasMuteSignal = embed.kind === "twitch-sdk" || embed.kind === "iframe" && !!embed.youtubeApi;
    const wantsAudio = hasIntent && (hasMuteSignal ? !muted : true);
    useElectronAudioLift(wantsAudio);

    if (embed.kind === "none") {
        return <div className="hcs-inline-player hcs-inline-player--empty" aria-label="No playable source" />;
    }

    if (embed.kind === "twitch-sdk") {
        // Twitch.Player fills this div; on SDK-load failure we fall back to the bare iframe.
        return (
            <div className="hcs-inline-player-wrap" ref={wrapperRef} onPointerDown={markIntent}>
                {hasBeenVisible && (twitchSdkFailed
                    ? <iframe
                        className="hcs-inline-player"
                        title={source.title ?? "Stream"}
                        src={embed.fallbackSrc}
                        allow="fullscreen; picture-in-picture; encrypted-media"
                        frameBorder={0}
                        loading="lazy"
                    />
                    : <div id={twitchDomId} className="hcs-inline-player" />)
                }
            </div>
        );
    }

    if (embed.kind === "iframe") {
        const popoutUrl = getStreamExternalUrl(source);
        return (
            <div className="hcs-inline-player-wrap" ref={wrapperRef} onPointerDown={markIntent}>
                {hasBeenVisible &&
                    <iframe
                        id={embed.youtubeApi ? ytIframeId : undefined}
                        className="hcs-inline-player"
                        title={source.title ?? "Stream"}
                        src={embed.src}
                        /*
                         * `allow=fullscreen` is the modern attribute and
                         * already covers what `allowFullScreen` did.
                         * Setting both makes React log "Allow attribute
                         * will take precedence". autoplay isn't requested
                         * (see URL builder above) so it's not in the
                         * allow list either.
                         */
                        allow="fullscreen; picture-in-picture; encrypted-media"
                        frameBorder={0}
                        loading="lazy"
                    />
                }
                {/*
                  * Overlay popout is the escape hatch for offline/VOD/clip
                  * embeds. For a LIVE stream it is suppressed here — the parent
                  * StreamPanel surfaces the same "open on platform" link up in
                  * the LIVE info bar above the video (so it never sits over the
                  * gameplay). Escape hatch for live still exists, just relocated.
                  */}
                {popoutUrl && !source.isLive &&
                    <a
                        className="hcs-inline-player__popout"
                        href={popoutUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open on ${platformLabel(source.platform)}`}
                        aria-label={`Open on ${platformLabel(source.platform)} (escape hatch when the embed is blocked by content classification)`}
                    >
                        <span aria-hidden>↗</span> {platformLabel(source.platform)}
                    </a>
                }
            </div>
        );
    }

    return (
        <video
            ref={videoRef}
            className="hcs-inline-player"
            controls
            autoPlay
            muted={source.muted}
            playsInline
            title={source.title}
        />
    );
};
